import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle } from 'lucide-react';
import { useB2BSyncDetails, useChipChipSyncDetails } from '@/hooks/useInventorySyncReport';
import { format, subDays } from 'date-fns';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

export function SyncDetailTables() {
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data: b2bData, isLoading: b2bLoading } = useB2BSyncDetails(startDate, endDate);
  const { data: chipchipData, isLoading: chipchipLoading } = useChipChipSyncDetails(startDate, endDate);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle>Sync Details</CardTitle>
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[160px]"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="b2b">
          <TabsList>
            <TabsTrigger value="b2b">
              B2B Deliveries
              {b2bData && <Badge variant="secondary" className="ml-2">{b2bData.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="chipchip">
              ChipChip Orders
              {chipchipData && <Badge variant="secondary" className="ml-2">{chipchipData.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="b2b">
            {b2bLoading ? <Skeleton className="h-48" /> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty Sold</TableHead>
                      <TableHead className="text-right">Returns</TableHead>
                      <TableHead className="text-right">Net Delivered</TableHead>
                      <TableHead className="text-center">Inventory</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(b2bData || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No B2B records for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      (b2bData || []).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.delivery_date}</TableCell>
                          <TableCell className="font-medium">{r.product_name}</TableCell>
                          <TableCell className="text-right">{r.total_quantity_sold ?? 0}</TableCell>
                          <TableCell className="text-right">{r.return_quantity ?? 0}</TableCell>
                          <TableCell className="text-right">{r.net_quantity_delivered ?? 0}</TableCell>
                          <TableCell className="text-center">
                            {r.inventoryMatched ? (
                              <CheckCircle className="h-4 w-4 text-green-500 inline" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive inline" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="chipchip">
            {chipchipLoading ? <Skeleton className="h-48" /> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Delivery Date</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Order Type</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-center">Inventory</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(chipchipData || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No ChipChip records for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      (chipchipData || []).map((o) => (
                        <TableRow key={o.id}>
                          <TableCell>{o.delivery_date}</TableCell>
                          <TableCell className="font-medium">{o.product_name}</TableCell>
                          <TableCell>{o.vendor_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{o.order_type}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{o.total_quantity}</TableCell>
                          <TableCell className="text-center">
                            {o.inventoryMatched ? (
                              <CheckCircle className="h-4 w-4 text-green-500 inline" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive inline" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
